// ไฟล์: frontend/src/components/common/ChangePasswordModal.js
import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Modal.css';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // ล้างข้อผิดพลาดเมื่อผู้ใช้แก้ไขข้อมูล
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  // ตรวจสอบความถูกต้องของข้อมูล
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'กรุณากรอกรหัสผ่านปัจจุบัน';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'กรุณากรอกรหัสผ่านใหม่';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'กรุณายืนยันรหัสผ่านใหม่';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'รหัสผ่านไม่ตรงกัน';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ส่งข้อมูลฟอร์ม
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบความถูกต้องของข้อมูล
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // ส่งข้อมูลไปยัง API
      const response = await axios.post('/users/change-password', formData);
      
      // รีเซ็ตฟอร์ม
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // ปิด Modal
      onClose();
      
      // แสดงแจ้งเตือนสำเร็จ
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: response.data.message,
        confirmButtonText: 'ตกลง'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      
      // แสดงข้อความผิดพลาดจาก API (ถ้ามี)
      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน';
      
      if (error.response?.status === 401) {
        setErrors(prevErrors => ({
          ...prevErrors,
          currentPassword: errorMessage
        }));
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: errorMessage,
          confirmButtonText: 'ตกลง'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2><i className="fas fa-key"></i> เปลี่ยนรหัสผ่าน</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="currentPassword" className="form-label">รหัสผ่านปัจจุบัน</label>
            <div className="password-input">
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                className={`form-control ${errors.currentPassword ? 'is-invalid' : ''}`}
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="กรอกรหัสผ่านปัจจุบัน"
              />
              <i className="fas fa-lock"></i>
            </div>
            {errors.currentPassword && <div className="invalid-feedback">{errors.currentPassword}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">รหัสผ่านใหม่</label>
            <div className="password-input">
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="กรอกรหัสผ่านใหม่"
              />
              <i className="fas fa-key"></i>
            </div>
            {errors.newPassword && <div className="invalid-feedback">{errors.newPassword}</div>}
            <div className="form-text">รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร</div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">ยืนยันรหัสผ่านใหม่</label>
            <div className="password-input">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
              />
              <i className="fas fa-check"></i>
            </div>
            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn btn-light" onClick={onClose}>
              <i className="fas fa-times"></i> ยกเลิก
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i> ยืนยัน
                </>
              )}
            </button>
          </div>
        </form>
        
        <div className="modal-tips">
          <div className="tip-title"><i className="fas fa-lightbulb"></i> คำแนะนำในการตั้งรหัสผ่าน</div>
          <ul className="tip-list">
            <li>ใช้รหัสผ่านที่มีความยาวอย่างน้อย 8 ตัวอักษร</li>
            <li>ผสมตัวอักษรพิมพ์ใหญ่และพิมพ์เล็ก</li>
            <li>ใช้ตัวเลขและอักขระพิเศษร่วมด้วย</li>
            <li>หลีกเลี่ยงการใช้ข้อมูลส่วนตัวที่คาดเดาได้ง่าย</li>
            <li>ไม่ควรใช้รหัสผ่านเดิมซ้ำ</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
